import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Box } from '@leanin/ui';
import {
  GroupImageUploadField,
  type GroupImageUploadFieldHandle,
} from '../../groups/GroupImageUploadField';

// The cover+logo upload state machine that every admin Overview section was
// re-implementing. Owns: the per-field refs, the per-field pending state,
// and the commit-then-clear lifecycle. Parents get exactly two things via
// `onPendingChange` + the imperative `commit()` ref:
//
//   const imagesRef = useRef<AdminImagePanelHandle>(null);
//   const [imagesPending, setImagesPending] = useState(false);
//   // dirty includes images automatically:
//   const dirty = textDirty || imagesPending;
//   // save handler awaits commit() before the text mutation:
//   if (imagesPending) await imagesRef.current?.commit();
//
// Networks and Spaces carry a logo; Circles are cover-only (peer groups,
// not brands). The prop union disambiguates which branches carry
// `currentLogoUrl`.

export type AdminImagePanelHandle = {
  readonly hasPending: boolean;
  /**
   * Uploads any staged cover/logo files. Edit surfaces call `commit()` and
   * the fields use their real `groupId` prop. Create surfaces don't have an
   * id until the network is inserted, so they render with a placeholder id
   * and pass the freshly-created id as `idOverride` here.
   */
  readonly commit: (idOverride?: string) => Promise<void>;
};

type CommonProps = {
  readonly groupName: string;
  readonly currentCoverUrl: string | null;
  /** Notifies parent whenever the cover-or-logo pending state changes —
   *  parents wire this into their `dirty` calculation. */
  readonly onPendingChange?: (anyPending: boolean) => void;
  /** Optional eyebrow override for the cover field. Falls back to the
   *  upload field's default "Cover" when omitted, so Network/Space admin
   *  pages keep their existing label without opting in. */
  readonly coverEyebrow?: string;
};

export type AdminImagePanelProps =
  | (CommonProps & {
      readonly groupKind: 'circle';
      readonly groupId: string;
    })
  | (CommonProps & {
      readonly groupKind: 'network';
      readonly groupId: string;
      readonly currentLogoUrl: string | null;
    })
  | (CommonProps & {
      readonly groupKind: 'space';
      readonly id: string;
      readonly currentLogoUrl: string | null;
    });

export const AdminImagePanel = forwardRef<
  AdminImagePanelHandle,
  AdminImagePanelProps
>((props, ref) => {
  const coverRef = useRef<GroupImageUploadFieldHandle | null>(null);
  const logoRef = useRef<GroupImageUploadFieldHandle | null>(null);
  const [coverPending, setCoverPending] = useState(false);
  const [logoPending, setLogoPending] = useState(false);

  const notify = (cover: boolean, logo: boolean) => {
    props.onPendingChange?.(cover || logo);
  };

  useImperativeHandle(
    ref,
    () => ({
      hasPending: coverPending || logoPending,
      commit: async (idOverride?: string) => {
        if (coverPending) await coverRef.current?.commit(idOverride);
        if (logoPending) await logoRef.current?.commit(idOverride);
      },
    }),
    [coverPending, logoPending],
  );

  return (
    <>
      <Box p="xl" style={{ borderBottom: '1px solid var(--border)' }}>
        {props.groupKind === 'space' ? (
          <GroupImageUploadField
            ref={coverRef}
            groupKind="space"
            id={props.id}
            groupName={props.groupName}
            field="cover"
            currentImageUrl={props.currentCoverUrl}
            eyebrowOverride={props.coverEyebrow}
            onPendingChange={(p) => {
              setCoverPending(p);
              notify(p, logoPending);
            }}
          />
        ) : (
          <GroupImageUploadField
            ref={coverRef}
            groupKind={props.groupKind}
            groupId={props.groupId}
            groupName={props.groupName}
            field="cover"
            currentImageUrl={props.currentCoverUrl}
            eyebrowOverride={props.coverEyebrow}
            onPendingChange={(p) => {
              setCoverPending(p);
              notify(p, logoPending);
            }}
          />
        )}
      </Box>

      {props.groupKind === 'network' ? (
        <Box p="xl" style={{ borderBottom: '1px solid var(--border)' }}>
          <GroupImageUploadField
            ref={logoRef}
            groupKind={props.groupKind}
            groupId={props.groupId}
            groupName={props.groupName}
            field="logo"
            currentImageUrl={props.currentLogoUrl}
            onPendingChange={(p) => {
              setLogoPending(p);
              notify(coverPending, p);
            }}
          />
        </Box>
      ) : props.groupKind === 'space' ? (
        <Box p="xl" style={{ borderBottom: '1px solid var(--border)' }}>
          <GroupImageUploadField
            ref={logoRef}
            groupKind="space"
            id={props.id}
            groupName={props.groupName}
            field="logo"
            currentImageUrl={props.currentLogoUrl}
            onPendingChange={(p) => {
              setLogoPending(p);
              notify(coverPending, p);
            }}
          />
        </Box>
      ) : null}
    </>
  );
});
AdminImagePanel.displayName = 'AdminImagePanel';
